import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProductById } from '../../services/productsApi';
import { useCart } from '../../contexts/CartContext';
import DesktopLayout from '../../components/layout/DesktopLayout';
import ReactMarkdown from 'react-markdown';
import { generateLiffProductUrl, copyToClipboard } from '../../utils/liffUrlUtils';
import type { Product, ProductVariant } from '../../types';
import './ProductDetailPage.css';

/**
 * Product Detail Page - With Variants Support
 */
const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addToCartMessage, setAddToCartMessage] = useState<string | null>(null);
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});
  const [shareMessage, setShareMessage] = useState<string | null>(null);

  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const response = await getProductById(id);

        if ('success' in response && response.success) {
          setProduct(response.data);
        } else {
          setError('找不到商品');
        }
      } catch (err: any) {
        console.error('Failed to fetch product:', err);
        setError(err?.message || '載入商品資料失敗');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  if (loading) {
    return (
      <DesktopLayout>
        <div className="product-detail-page">
          <div className="product-detail-page__loading">載入中...</div>
        </div>
      </DesktopLayout>
    );
  }

  if (error || !product) {
    return (
      <DesktopLayout>
        <div className="product-detail-page">
          <div className="product-detail-page__error">
            <p>{error || '找不到商品'}</p>
            <button onClick={() => (window.location.href = '/products')}>
              返回商品列表
            </button>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  // ============ Variant Logic ============

  // Extract unique attribute options from variants
  const getAttributeOptions = () => {
    if (!product.variants || product.variants.length === 0) return {};

    const attributeMap: Record<string, Set<string>> = {};

    product.variants.forEach(variant => {
      Object.entries(variant.attributes).forEach(([key, value]) => {
        if (!attributeMap[key]) {
          attributeMap[key] = new Set();
        }
        attributeMap[key].add(value);
      });
    });

    // Convert Sets to arrays
    const result: Record<string, string[]> = {};
    Object.entries(attributeMap).forEach(([key, values]) => {
      result[key] = Array.from(values).sort();
    });

    return result;
  };

  // Find variant based on selected attributes
  const findMatchingVariant = (): ProductVariant | null => {
    if (!product.variants || product.variants.length === 0) return null;

    // If no attributes selected, return null (use parent product)
    if (Object.keys(selectedAttributes).length === 0) {
      return null;
    }

    // Find exact match
    const matching = product.variants.find(variant => {
      return Object.entries(selectedAttributes).every(([key, value]) => {
        return variant.attributes[key] === value;
      });
    });

    return matching || null;
  };

  // Handle attribute selection
  const handleAttributeSelect = (attributeName: string, value: string) => {
    const newAttributes = { ...selectedAttributes, [attributeName]: value };

    // Reset dependent attributes (simplified approach - reset all attributes after the current one)
    const attributeKeys = Object.keys(getAttributeOptions());
    const currentIndex = attributeKeys.indexOf(attributeName);

    if (currentIndex >= 0) {
      const resetAttributes: Record<string, string> = {};
      attributeKeys.slice(0, currentIndex + 1).forEach(key => {
        if (newAttributes[key]) {
          resetAttributes[key] = newAttributes[key];
        }
      });
      setSelectedAttributes(resetAttributes);
    } else {
      setSelectedAttributes(newAttributes);
    }
  };

  // Get the currently active variant
  const activeVariant = findMatchingVariant();
  const activeVariantId = activeVariant?.id || null;

  // Determine if we should use variant pricing/display
  const displayVariant = activeVariant;
  const displayPrice = displayVariant?.salePrice || displayVariant?.regularPrice || product.salePrice || product.regularPrice;
  const hasSalePrice = displayVariant
    ? (displayVariant.salePrice !== null && displayVariant.regularPrice && displayVariant.salePrice < displayVariant.regularPrice)
    : (product.salePrice !== null && product.regularPrice !== null && product.salePrice < product.regularPrice);

  // Stock status based on variant or parent product
  const currentStockStatus = displayVariant?.stockStatus || product.stockStatus;
  const currentStockQuantity = displayVariant?.stockQuantity || product.stockQuantity;

  // Stock status text
  const getStockStatusText = () => {
    switch (currentStockStatus) {
      case 'instock':
        return currentStockQuantity > 0 ? `庫存: ${currentStockQuantity} 件` : '有庫存';
      case 'outofstock':
        return '已售完';
      case 'onbackorder':
        return '補貨中';
      default:
        return '';
    }
  };

  // Image list - use variant images if available
  const allImages = displayVariant?.imageUrls && displayVariant.imageUrls.length > 0
    ? displayVariant.imageUrls
    : (product.featuredImageUrl
      ? [product.featuredImageUrl, ...product.imageUrls.filter(url => url !== product.featuredImageUrl)]
      : product.imageUrls);

  // ============ Handlers ============

  // Add to cart handler
  const handleAddToCart = async () => {
    if (!product) return;

    setAddingToCart(true);
    setAddToCartMessage(null);

    try {
      await addToCart(product.id, activeVariantId || undefined, activeVariant ? activeVariant.attributes : undefined, quantity);
      setAddToCartMessage('已加入購物車！');
      setTimeout(() => setAddToCartMessage(null), 3000);
    } catch (err: any) {
      setAddToCartMessage(err.message || '加入購物車失敗');
    } finally {
      setAddingToCart(false);
    }
  };

  // Buy now handler
  const handleBuyNow = async () => {
    if (!product) return;

    setAddingToCart(true);
    setAddToCartMessage(null);

    try {
      await addToCart(product.id, activeVariantId || undefined, activeVariant ? activeVariant.attributes : undefined, quantity);
      navigate('/checkout');
    } catch (err: any) {
      setAddToCartMessage(err.message || '加入購物車失敗');
      setAddingToCart(false);
    }
  };

  // Copy LIFF link handler
  const handleCopyLiffLink = async (viewType: 'compact' | 'tall' | 'full' = 'compact') => {
    if (!product) return;

    const liffUrl = generateLiffProductUrl(product.id, viewType);
    const success = await copyToClipboard(liffUrl);

    if (success) {
      setShareMessage(`已複製 ${viewType === 'compact' ? '精簡' : viewType === 'tall' ? '加高' : '全螢幕'}視圖連結！`);
      setTimeout(() => setShareMessage(null), 3000);
    } else {
      setShareMessage('複製失敗，請手動複製連結');
      setTimeout(() => setShareMessage(null), 3000);
    }
  };

  // Attribute options for variant selectors
  const attributeOptions = getAttributeOptions();
  const hasVariants = product.variants && product.variants.length > 0;

  return (
    <DesktopLayout>
      <div className="product-detail-page">
        {/* Breadcrumb */}
        <div className="product-detail-page__breadcrumb">
          <a href="/products">商品列表</a>
          <span> / </span>
          <span>{product.title}</span>
        </div>

        <div className="product-detail-page__content">
          {/* Left side: Images */}
          <div className="product-detail-page__images">
            {allImages.length > 0 && (
              <>
                <div className="product-detail-page__main-image">
                  <img
                    src={allImages[selectedImageIndex]}
                    alt={product.title}
                  />
                  {currentStockStatus === 'outofstock' && (
                    <div className="product-detail-page__sold-out">已售完</div>
                  )}
                </div>

                {allImages.length > 1 && (
                  <div className="product-detail-page__thumbnails">
                    {allImages.map((image, index) => (
                      <button
                        key={index}
                        className={`product-detail-page__thumbnail ${index === selectedImageIndex ? 'active' : ''}`}
                        onClick={() => setSelectedImageIndex(index)}
                      >
                        <img src={image} alt={`${product.title} ${index + 1}`} />
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right side: Product info */}
          <div className="product-detail-page__info">
            <h1 className="product-detail-page__title">{product.title}</h1>

            <div className="product-detail-page__meta">
              <span className="product-detail-page__category">
                📦 {product.productCategory}
              </span>
              {product.sku && (
                <span className="product-detail-page__sku">
                  商品編號: {product.sku}
                </span>
              )}
              {displayVariant?.sku && displayVariant.sku !== product.sku && (
                <span className="product-detail-page__sku">
                  變體編號: {displayVariant.sku}
                </span>
              )}

              {/* LIFF Share Button */}
              <div className="product-detail-page__liff-share">
                <button
                  className="product-detail-page__liff-share-btn"
                  onClick={() => handleCopyLiffLink('compact')}
                  title="複製 LIFF 精簡視圖連結"
                  type="button"
                >
                  <span className="product-detail-page__liff-share-icon">📱</span>
                  複製 LINE 連結
                </button>
                <div className="product-detail-page__liff-share-dropdown">
                  <button
                    onClick={() => handleCopyLiffLink('compact')}
                    type="button"
                  >
                    精簡視圖 (50%)
                  </button>
                  <button
                    onClick={() => handleCopyLiffLink('tall')}
                    type="button"
                  >
                    加高視圖 (80%)
                  </button>
                  <button
                    onClick={() => handleCopyLiffLink('full')}
                    type="button"
                  >
                    全螢幕視圖
                  </button>
                </div>
              </div>

              {shareMessage && (
                <div className={`product-detail-page__share-message ${shareMessage.includes('失敗') ? 'error' : 'success'}`}>
                  {shareMessage}
                </div>
              )}
            </div>

            {/* Price */}
            {displayPrice && (
              <div className="product-detail-page__price">
                {hasSalePrice && (displayVariant?.regularPrice || product.regularPrice) && (
                  <span className="product-detail-page__price--original">
                    NT${displayVariant?.regularPrice || product.regularPrice}
                  </span>
                )}
                <span className="product-detail-page__price--current">
                  NT${displayPrice}
                </span>
                {hasSalePrice && (
                  <span className="product-detail-page__discount">
                    特價中
                  </span>
                )}
              </div>
            )}

            {/* Stock status */}
            <div className="product-detail-page__stock">
              {getStockStatusText()}
            </div>

            {/* Variant Selectors */}
            {hasVariants && Object.entries(attributeOptions).length > 0 && (
              <div className="product-detail-page__variants">
                {Object.entries(attributeOptions).map(([attrName, options]) => (
                  <div key={attrName} className="product-detail-page__variant-group">
                    <h3>{attrName}</h3>
                    <div className="product-detail-page__variant-options">
                      {options.map(option => {
                        const isSelected = selectedAttributes[attrName] === option;
                        // Check if this option leads to any in-stock variant
                        const isAvailable = product.variants!.some(v =>
                          v.attributes[attrName] === option && v.stockStatus === 'instock'
                        );

                        return (
                          <button
                            key={option}
                            className={`product-detail-page__variant-option ${isSelected ? 'product-detail-page__variant-option--selected' : ''} ${!isAvailable ? 'product-detail-page__variant-option--outofstock' : ''}`}
                            onClick={() => handleAttributeSelect(attrName, option)}
                            disabled={!isAvailable}
                            type="button"
                          >
                            {option}
                            {displayVariant?.attributes[attrName] === option && displayVariant.salePrice && (
                              <span className="product-detail-page__variant-option__sale">特價</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Short description */}
            {product.shortDescription && (
              <div className="product-detail-page__short-description">
                {product.shortDescription}
              </div>
            )}

            {/* Full description - Render as Markdown */}
            {product.description && (
              <div className="product-detail-page__description">
                <h2>商品描述</h2>
                <ReactMarkdown>{product.description}</ReactMarkdown>
              </div>
            )}

            {/* Tags */}
            {product.tags && product.tags.length > 0 && (
              <div className="product-detail-page__tags">
                {product.tags.map((tag, index) => (
                  <span key={index} className="product-detail-page__tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Specs */}
            {(product.dimensions || product.weight) && (
              <div className="product-detail-page__specs">
                <h3>規格</h3>
                {product.dimensions && (
                  <div className="product-detail-page__spec">
                    <span>尺寸: {product.dimensions}</span>
                  </div>
                )}
                {product.weight && (
                  <div className="product-detail-page__spec">
                    <span>重量: {product.weight} kg</span>
                  </div>
                )}
              </div>
            )}

            {/* View count */}
            <div className="product-detail-page__views">
              瀏覽次數: {product.viewCount}
            </div>

            {/* Quantity and add to cart */}
            {currentStockStatus !== 'outofstock' && (
              <div className="product-detail-page__actions">
                <div className="product-detail-page__quantity">
                  <label htmlFor="quantity">數量:</label>
                  <select
                    id="quantity"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                  >
                    {[...Array(Math.min(10, currentStockQuantity || 10)).keys()].map(i => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                </div>

                <div className="product-detail-page__buttons">
                  <button
                    className="product-detail-page__add-to-cart"
                    onClick={handleAddToCart}
                    disabled={addingToCart || (hasVariants && !activeVariantId)}
                  >
                    {addingToCart ? '加入中...' : '加入購物車'}
                  </button>
                  <button
                    className="product-detail-page__buy-now"
                    onClick={handleBuyNow}
                    disabled={addingToCart || (hasVariants && !activeVariantId)}
                  >
                    {addingToCart ? '處理中...' : '立即購買'}
                  </button>
                </div>

                {addToCartMessage && (
                  <div className={`product-detail-page__message ${addToCartMessage.includes('失敗') ? 'error' : 'success'}`}>
                    {addToCartMessage}
                  </div>
                )}
              </div>
            )}

            {currentStockStatus === 'outofstock' && (
              <div className="product-detail-page__out-of-stock">
                此商品已售完，暫時無法購買
              </div>
            )}
          </div>
        </div>
      </div>
    </DesktopLayout>
  );
};

export default ProductDetailPage;
